// src/components/NewSection.jsx - COMPLETELY REDESIGNED
import React, { useRef, useEffect } from 'react';

const NewSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section className="modern-app-section" ref={sectionRef} id="new-section">
      <div className="container">
        <div className="row align-items-center g-5">
          {/* Left Content */}
          <div className="col-lg-6">
            <div className="app-content">
              {/* Badge */}
              <div className="app-badge">
                <i className="bx bx-mobile"></i>
                <span>Available on Mobile</span>
              </div>

              <h2 className="app-title">
                Legal Research <span className="gradient-text">On The Go</span>
              </h2>

              <p className="app-description">
                Access your AI legal assistant anytime, anywhere. Download our mobile app 
                for iOS and Android and take the power of legal research with you.
              </p>

              {/* Features List */}
              <div className="app-features-list">
                <div className="feature-item">
                  <div className="feature-icon">
                    <i className="bx bx-check"></i>
                  </div>
                  <div className="feature-text">
                    <h4>iOS & Android Apps</h4>
                    <p>Available on both platforms</p>
                  </div>
                </div>

                <div className="feature-item">
                  <div className="feature-icon">
                    <i className="bx bx-check"></i>
                  </div>
                  <div className="feature-text">
                    <h4>Voice Search</h4>
                    <p>Search hands-free with voice commands</p>
                  </div>
                </div>

                <div className="feature-item">
                  <div className="feature-icon">
                    <i className="bx bx-check"></i>
                  </div>
                  <div className="feature-text">
                    <h4>Offline Mode</h4>
                    <p>Access saved documents offline</p>
                  </div>
                </div>
              </div>

              {/* Download Buttons */}
              <div className="download-buttons">
                <button className="download-btn apple-btn">
                  <i className="bx bxl-apple"></i>
                  <div className="btn-text">
                    <span className="small-text">Download on the</span>
                    <span className="large-text">App Store</span>
                  </div>
                </button>

                <button className="download-btn google-btn">
                  <i className="bx bxl-play-store"></i>
                  <div className="btn-text">
                    <span className="small-text">Get it on</span>
                    <span className="large-text">Google Play</span>
                  </div>
                </button>
              </div>

              {/* Stats */}
              <div className="app-stats">
                <div className="stat">
                  <div className="stat-number gradient-text">4.8</div>
                  <div className="stat-label">★★★★★ Rating</div>
                </div>
                <div className="stat-divider"></div>
                <div className="stat">
                  <div className="stat-number gradient-text">50K+</div>
                  <div className="stat-label">Downloads</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Visual */}
          <div className="col-lg-6">
            <div className="app-visual">
              {/* Decorative Elements */}
              <div className="visual-decoration decoration-1"></div>
              <div className="visual-decoration decoration-2"></div>
              
              {/* Mobile Mockup */}
              <div className="mobile-mockup">
                <div className="mockup-content">
                  <div className="mockup-screen">
                    <i className="bx bx-mobile-alt"></i>
                    <p>Mobile App Interface</p>
                  </div>
                </div>
                
                {/* Floating Elements */}
                <div className="floating-element element-1">
                  <i className="bx bx-search-alt"></i>
                  <span>Quick Search</span>
                </div>
                
                <div className="floating-element element-2">
                  <i className="bx bx-bell"></i>
                  <span>Notifications</span>
                </div>
                
                <div className="floating-element element-3">
                  <i className="bx bx-bookmark"></i>
                  <span>Bookmarks</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* ============================================
           MODERN APP SECTION
        ============================================ */
        .modern-app-section {
          padding: 80px 0;
          background: #ffffff;
          position: relative;
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.8s ease;
          overflow: hidden;
        }

        .modern-app-section.fade-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* App Content */
        .app-content {
          animation: slideInLeft 0.8s ease 0.3s both;
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .app-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.1));
          border-radius: 50px;
          margin-bottom: 20px;
          font-size: 14px;
          font-weight: 600;
          color: #E91E63;
        }

        .app-badge i {
          font-size: 18px;
        }

        .app-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800;
          color: #1a1a1a;
          margin-bottom: 20px;
          line-height: 1.2;
          letter-spacing: -0.02em;
        }

        .app-description {
          font-size: clamp(1rem, 2vw, 1.15rem);
          color: #666;
          line-height: 1.7;
          margin-bottom: 32px;
        }

        /* Features List */
        .app-features-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-bottom: 32px;
        }

        .feature-item {
          display: flex;
          align-items: flex-start;
          gap: 16px;
        }

        .feature-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .feature-icon i {
          font-size: 24px;
          color: white;
        }

        .feature-text h4 {
          font-size: 16px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .feature-text p {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

        /* Download Buttons */
        .download-buttons {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 32px;
        }

        .download-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 24px;
          background: #1a1a1a;
          border: 2px solid #1a1a1a;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          color: white;
        }

        .download-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
        }

        .download-btn i {
          font-size: 32px;
        }

        .btn-text {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          text-align: left;
        }

        .small-text {
          font-size: 11px;
          opacity: 0.8;
        }

        .large-text {
          font-size: 16px;
          font-weight: 600;
        }

        /* App Stats */
        .app-stats {
          display: flex;
          align-items: center;
          gap: 24px;
        }

        .stat {
          text-align: left;
        }

        .stat-number {
          font-size: 32px;
          font-weight: 800;
          line-height: 1;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 14px;
          color: #666;
        }

        .stat-divider {
          width: 2px;
          height: 40px;
          background: linear-gradient(to bottom, transparent, #ddd, transparent);
        }

        /* App Visual */
        .app-visual {
          position: relative;
          height: 600px;
          animation: slideInRight 0.8s ease 0.5s both;
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        /* Visual Decorations */
        .visual-decoration {
          position: absolute;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.1));
          animation: float 15s ease-in-out infinite;
        }

        .decoration-1 {
          width: 300px;
          height: 300px;
          top: 0;
          right: 0;
        }

        .decoration-2 {
          width: 200px;
          height: 200px;
          bottom: 0;
          left: 50px;
          animation-delay: 5s;
        }

        /* Mobile Mockup */
        .mobile-mockup {
          position: relative;
          width: 300px;
          height: 600px;
          margin: 0 auto;
          background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
          border-radius: 40px;
          padding: 12px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .mockup-content {
          width: 100%;
          height: 100%;
          background: white;
          border-radius: 32px;
          overflow: hidden;
        }

        .mockup-screen {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #E91E63 0%, #c41849 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .mockup-screen i {
          font-size: 80px;
          margin-bottom: 16px;
        }

        .mockup-screen p {
          font-size: 16px;
          font-weight: 600;
          margin: 0;
        }

        /* Floating Elements */
        .floating-element {
          position: absolute;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          font-size: 14px;
          font-weight: 600;
          color: #333;
          animation: floatElement 4s ease-in-out infinite;
        }

        .floating-element i {
          font-size: 20px;
          color: #E91E63;
        }

        .element-1 {
          top: 80px;
          right: -20px;
          animation-delay: 0s;
        }

        .element-2 {
          top: 280px;
          left: -40px;
          animation-delay: 1s;
        }

        .element-3 {
          bottom: 120px;
          right: -30px;
          animation-delay: 2s;
        }

        @keyframes floatElement {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-15px);
          }
        }

        /* Responsive */
        @media (max-width: 991px) {
          .modern-app-section {
            padding: 60px 0;
          }

          .app-visual {
            height: 500px;
            margin-top: 40px;
          }

          .mobile-mockup {
            width: 250px;
            height: 500px;
          }
        }

        @media (max-width: 767px) {
          .download-buttons {
            flex-direction: column;
          }

          .download-btn {
            width: 100%;
            justify-content: center;
          }

          .app-stats {
            justify-content: space-between;
            width: 100%;
          }

          .stat-divider {
            display: none;
          }

          .app-visual {
            height: 450px;
          }

          .mobile-mockup {
            width: 220px;
            height: 440px;
          }

          .floating-element {
            padding: 8px 12px;
            font-size: 12px;
          }
        }
      `}</style>
    </section>
  );
};

export default NewSection;